import type { GroupChatTransport } from './group-chat';

/** A transport that forgets everything it holds for the signed-in person on `reset()`. */
export interface ResettableGroupChatTransport extends GroupChatTransport {
  reset(): void;
}

/**
 * A transport whose implementation is loaded the first time a screen uses it, so an app with
 * group chat switched on loads Reach's client only when someone opens a group, and an app with
 * it off never loads it at all. A failed load is tried again on the next call.
 */
export function lazyGroupChatTransport(
  load: () => Promise<ResettableGroupChatTransport>,
): ResettableGroupChatTransport {
  let loading: Promise<ResettableGroupChatTransport> | undefined;
  let loaded: ResettableGroupChatTransport | undefined;
  const transport = () => {
    loading ??= load().then(
      (implementation) => {
        loaded = implementation;
        return implementation;
      },
      (error: unknown) => {
        loading = undefined;
        throw error;
      },
    );
    return loading;
  };
  return {
    list: async (groupId, cursor) => (await transport()).list(groupId, cursor),
    send: async (groupId, text) => (await transport()).send(groupId, text),
    markRead: async (groupId) => (await transport()).markRead(groupId),
    subscribe: async (groupId, onChange, onError) =>
      (await transport()).subscribe(groupId, onChange, onError),
    reset: () => loaded?.reset(),
  };
}
